import { apiFetch, ApiError } from '../api/client'

/**
 * Les guerres en cours, telles que la carte les montre.
 *
 * **Une déclaration vaut une guerre.** La base connaît des DÉCLARATIONS, pas
 * des conflits : une ligne dit « A a déclaré la guerre à B », et une contrainte
 * impose une cible unique. Rien ne relie deux déclarations en un même conflit,
 * et rien ne dit qu'un troisième pays entré en guerre contre B est l'allié de A.
 *
 * On s'en tient donc à deux camps par guerre. Regrouper automatiquement par
 * belligérant commun inventerait une règle que personne n'a décidée — deux pays
 * qui attaquent le même voisin ne sont pas forcément alliés. Le regroupement en
 * conflits demandera une table et un arbitrage, pas une déduction.
 *
 * `?status=active` est demandé au serveur et non filtré ici : une guerre
 * terminée affichée comme un conflit en cours ferait prendre une décision
 * fausse, et le filtre côté client est celui qu'on oublie.
 */
export const fetchActiveWars = async () => {
  const response = await apiFetch('/war-declarations?status=active', { timeout: 12000 })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || !payload?.success) {
    throw new ApiError(payload?.error || `HTTP ${response.status}`, { status: response.status })
  }
  // Une guerre qui vise une RÉGION plutôt qu'un pays n'a pas de second camp à
  // colorier ; elle reste listée, mais la carte n'en allume qu'un côté.
  return (payload.data || []).map((war) => ({
    id: war.war_declaration_id,
    attacker: war.country_a,
    attackerName: war.country_a_name || `Pays #${war.country_a}`,
    defender: war.country_b,
    defenderName: war.country_b_name || (war.target_region_id ? `Région #${war.target_region_id}` : null),
    targetRegionId: war.target_region_id,
    declaredOn: war.declaration_date,
    messageUrl: war.message_url,
  }))
}

/** Les couleurs des deux camps. Volontairement opposées, jamais deux rouges :
 *  la carte doit répondre « qui contre qui » d'un coup d'œil, et un daltonien
 *  distingue mieux une opposition chaud/froid que deux teintes voisines. */
export const WAR_COLORS = { attacker: '#e2574c', defender: '#4a90e2', other: '#3a3a3a' }
